import { nanoid } from 'nanoid';

import type {
  AdditionalList,
  AdditionalMagnitude,
  AdditionalText,
  CalculateServiceReq,
  NewAdditionalBoolean,
} from '@quick/go-api-types/services/calculate';
import type { IServiceType } from '@quick/go-api-types/services/country/lines/[id]';

import { TEXT_FIELD_ID } from './constants';
import type { IForm } from './types';

export function getNewAddress(): IForm['stops'][number] {
  return {
    description: '',
    address: '',
    assistants: 0,
    uuid: nanoid(),
    city_id: 0,
    name: 'Stop',
  };
}

export const defaultValues = {
  additional_fields: {},
  packages: [],
  additional_data: { order_no: '', coupon: '' },
  stops: [],
  dates: [{ date_time: '', uuid: nanoid() }],
};

export function getValidData(
  data: IForm,
  {
    serviceAdditionals,
    useFirstStop,
  }: { serviceAdditionals: string[]; useFirstStop?: boolean }
) {
  const programmed = data.dates.length > 0;

  const stops = data.stops.map(
    ({ description, type_stop_id, address, zip, ...r }) => ({
      ...r,
      ...(useFirstStop && data.stops[0]),
      address: useFirstStop ? data.stops[0].address.trim() : address.trim(),
      description: description || undefined,
      type_stop_id: type_stop_id || undefined,
      zip: zip || undefined,
    })
  );

  const additionalFields = Object.entries(data.additional_fields ?? {}).reduce(
    (p, [k, v]) => {
      let id = k.replace('_', '');
      const isTextField = id.includes(TEXT_FIELD_ID);
      id = id.replace(TEXT_FIELD_ID, '');
      if (!serviceAdditionals.includes(id.toString())) return p;
      if (typeof v === 'boolean') p[id] = [{ units: 1, value: v }];
      else if (typeof v === 'string' || v === undefined) {
        if (isTextField) {
          // do not send empty text additional fields
          if (!v) return p;
          p[id] = [{ units: 1, text: v ?? '' }];
        } else {
          p[id] = [{ units: 1, goods: v ?? '' }];
        }
      } else
        p[id] = [
          {
            units: 1,
            ...v,
            total: v?.total || 0,
            dimensions: Object.fromEntries(
              Object.entries(v.dimensions ?? {}).map(([k, v]) => [k, v || 0])
            ),
          },
        ];
      return p;
    },
    {} as CalculateServiceReq['additional_fields']
  );

  let package_stops = {};

  const packages = data.packages.reduce(
    (p, { f, units }, i) => {
      Object.entries(f).forEach(([k, v]) => {
        let key = k.replace('_', '');
        const isTextField = key.includes(TEXT_FIELD_ID); // Differentiate select fields from text fields
        key = key.replace(TEXT_FIELD_ID, '');
        let field = p[key];
        if (!field) {
          p[key] = [];
          field = p[key];
        }

        // Check against service additionals
        if (serviceAdditionals.includes(key.toString())) return p;
        // Booleans
        if (typeof v === 'boolean') {
          const f = field as NewAdditionalBoolean[];
          const val: NewAdditionalBoolean = {
            units,
            value: v ?? false,
            order: i,
          };
          f.push(val);
        }
        // Transform goods to array of list field
        else if (typeof v === 'string' || v === undefined) {
          if (isTextField) {
            // do not send empty text additional fields
            if (!v) return p;
            const f = field as AdditionalText[];
            f.push({ units, text: v ?? '', order: i });
          } else {
            const f = field as AdditionalList[];
            f.push({ units, goods: v ?? '', order: i });
            //Selector of address per package
            if (key === '2503') {
              package_stops = { ...package_stops, [i]: v };
            } //i is the order of package and v is stop index}
          }
        } else {
          const f = field as AdditionalMagnitude[];
          f.push({
            units,
            order: i,
            ...v,
            total: v?.total || 0,
            dimensions: Object.fromEntries(
              Object.entries(v.dimensions ?? {}).map(([k, v]) => [k, v || 0])
            ),
          });
        }
      });
      return p;
    },
    {} as CalculateServiceReq['additional_fields']
  );

  return {
    ...data,
    packages: undefined,
    package_stops,
    additional_fields: { ...packages, ...additionalFields },
    city_id: data.stops.at(0)?.city_id,
    dates: data.dates,
    programmed,
    stops,
    transport_conditions: data.transport_conditions || undefined,
    vehicle_type_id: data.vehicle_type_id || undefined,
    // TODO: Fix this when back fixes it
    worker_quantity: 1,
    worker_money: 0,
  } as CalculateServiceReq;
}

export function stripNumericalUnitSymbols(str: string) {
  return str
    .replace('^3', '')
    .replace('^2', '')
    .replace('³', '')
    .replace('²', '')
    .replace('3', '')
    .replace('2', '');
}

type AdditionalField = IServiceType['additional_fields'][number];
export function sortAdditionalFields(a: AdditionalField, b: AdditionalField) {
  if (a.field.order_priority && !b.field.order_priority) return -1; // Let non-prioritized fields be last
  if (!a.field.order_priority && b.field.order_priority) return 1; // Let non-prioritized fields be last
  if (b.field.order_priority && a.field.order_priority)
    return a.field.order_priority - b.field.order_priority; // If both fields have priority
  return a.field.name.localeCompare(b.field.name); // Both fields don't have priority
}
